import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@arcadeai/design-system/components/ui/atoms/input-group";
import { McpServerPickerHeader } from "@arcadeai/design-system/components/ui/organisms/mcp-server-picker";
import { Search, X } from "lucide-react";
import type {
  McpServerPickerActions,
  McpServerPickerState,
} from "../hooks/use-mcp-server-picker";
import type { Mode } from "../types";
import { HeaderCheckboxToggle } from "./header-checkbox-toggle";

const UNOPTIMIZED_TOOLTIP = (
  <>
    Tools built around individual actions rather than full tasks. Useful when
    you need something specific, but your agent often has to combine a few to
    complete a task.
  </>
);

/**
 * Picker header row: the catalog search box plus the "Select all" and
 * "Unoptimized" toggles. Renders nothing when none of them apply to the current
 * view (e.g. the review screen).
 */
export function PickerHeader({
  actions,
  mode,
  state,
}: {
  actions: McpServerPickerActions;
  mode: Mode;
  state: McpServerPickerState;
}) {
  const { view } = state;
  const showSelectAll =
    mode === "multi" && view.type !== "review" && state.visibleCount > 0;
  const showUnoptimizedToggle =
    view.type !== "review" && state.hasUnoptimizedServers;
  const selectAllIndeterminate =
    state.someVisibleSelected && !state.allVisibleSelected;
  // The search box stays put across views, but its target changes with context:
  // in the catalog it filters the servers list; drilled into a server it filters
  // that server's own tools (a separate query that resets per drill-in, so it
  // never reads as a leftover catalog filter). The review view brings its own
  // header, so no search box there.
  const inTools = view.type === "tools";
  const showSearch = view.type !== "review";
  const searchValue = inTools ? state.toolQuery : state.query;
  const setSearch = inTools ? actions.setToolQuery : actions.setQuery;
  const searchLabel = inTools ? "Search tools" : "Search MCP servers and tools";

  if (!(showSearch || showSelectAll || showUnoptimizedToggle)) {
    return null;
  }

  return (
    <McpServerPickerHeader>
      {showSearch ? (
        <InputGroup className="h-9 min-w-0 flex-1">
          <InputGroupAddon align="inline-start">
            <Search aria-hidden className="size-4 text-muted-foreground" />
          </InputGroupAddon>
          <InputGroupInput
            aria-label={searchLabel}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchLabel}
            value={searchValue}
          />
          {searchValue.length > 0 ? (
            <InputGroupAddon align="inline-end">
              <InputGroupButton
                aria-label="Clear search"
                onClick={() => setSearch("")}
                size="icon-xs"
              >
                <X aria-hidden className="size-3.5" />
              </InputGroupButton>
            </InputGroupAddon>
          ) : null}
        </InputGroup>
      ) : null}
      {showSelectAll || showUnoptimizedToggle ? (
        <div className="flex @3xl:w-auto w-full gap-2">
          {showSelectAll ? (
            <HeaderCheckboxToggle
              checked={state.allVisibleSelected}
              className="min-w-0 @3xl:flex-none flex-1"
              indeterminate={selectAllIndeterminate}
              label="Select all"
              onCheckedChange={actions.toggleSelectAllVisible}
            />
          ) : null}
          {showUnoptimizedToggle ? (
            <HeaderCheckboxToggle
              checked={state.showUnoptimizedMatches}
              className="min-w-0 @3xl:flex-none flex-1"
              label="Unoptimized"
              onCheckedChange={() =>
                actions.setShowUnoptimizedMatches(!state.showUnoptimizedMatches)
              }
              tooltip={UNOPTIMIZED_TOOLTIP}
            />
          ) : null}
        </div>
      ) : null}
    </McpServerPickerHeader>
  );
}
